import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

/**
 * PWA install prompt — shows a branded popup when:
 *  - `beforeinstallprompt` event fires (Android/Chrome/Edge/desktop)
 *  - OR iOS Safari is detected (which has no native prompt) and user is logged in
 *
 * Only shown when `authed` prop is true (pass localStorage check from parent).
 * Respects dismissal via localStorage flag.
 */
const DISMISS_KEY = "koko_install_dismissed_at";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isIOS() {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt({ authed }) {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    if (!authed || isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    // Chrome/Android/desktop path
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS path — no event; show after a small delay so user sees dashboard first
    if (isIOS()) {
      const t = setTimeout(() => { setIsIos(true); setOpen(true); }, 1500);
      return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(t); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [authed]);

  const onInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setOpen(false);
    }
    setDeferred(null);
  };

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-[380px] z-[60] border-2 border-red-600 bg-gradient-to-b from-[#141414] to-[#0A0A0A] p-5 shadow-[8px_8px_0_0_rgba(229,9,20,0.4)] animate-fadeInUp"
    >
      <button
        onClick={onDismiss}
        data-testid="install-dismiss"
        className="absolute top-2 right-2 text-zinc-500 hover:text-white transition-colors"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 shrink-0 bg-red-600 flex items-center justify-center border-2 border-white">
          <Smartphone size={22} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-red-500">New</p>
          <h3 className="text-sm font-black text-white uppercase tracking-tight mt-0.5">Install KoKo Sites App</h3>
          <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
            {isIos
              ? "Open your dashboard instantly with one tap. Add KoKo to your home screen for an app-like experience."
              : "One-click install. Your dashboard opens directly — no browser, no logins, just work."}
          </p>
        </div>
      </div>

      {isIos ? (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-1.5 text-[11px] text-zinc-400 leading-relaxed">
          <p>1. Tap the <span className="text-white font-bold">Share</span> button in Safari</p>
          <p>2. Scroll and tap <span className="text-white font-bold">"Add to Home Screen"</span></p>
          <p>3. Tap <span className="text-white font-bold">Add</span></p>
        </div>
      ) : (
        <div className="mt-4 flex gap-2">
          <button
            onClick={onInstall}
            data-testid="install-accept"
            className="flex-1 bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-4 border-2 border-white hover:bg-white hover:text-black transition-colors text-xs flex items-center justify-center gap-2"
          >
            <Download size={13} /> Install App
          </button>
          <button
            onClick={onDismiss}
            className="text-zinc-400 hover:text-white font-bold uppercase tracking-widest py-3 px-4 text-xs"
          >
            Later
          </button>
        </div>
      )}
    </div>
  );
}

// Helper for parent components — call on mount to detect standalone + route accordingly.
export function isRunningStandalone() {
  return isStandalone();
}
